import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import {
    fetchSubscribers,
    setSubscribed,
    deleteSubscriber,
    exportSubscribers,
    selectAllSubscribers,
    selectSubscriberCounts,
    selectSubscriberStatus,
    selectSubscriberSaving,
    selectSubscriberError,
} from './SubscriberSlice';
import { useIsMobile } from '../../theme';

// Rows rendered before "Mostrar mais" — the same scroll-first choice as the
// articles grid, since a mailing list only grows.
const PAGE_SIZE = 50;

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '—';

const DeleteConfirmDialog = ({ subscriber, onClose, onConfirm }) => (
    <Dialog open={!!subscriber} onClose={onClose}>
        <DialogTitle>Apagar subscritor</DialogTitle>
        <DialogContent>
            <DialogContentText>
                Apagar “{subscriber?.email}” de vez? Perde-se também a data de subscrição, e o endereço pode voltar a subscrever pelo
                formulário do site. Para o manter na lista mas deixar de o contactar, use “anular subscrição”.
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} color='inherit'>
                Cancelar
            </Button>
            <Button onClick={onConfirm} color='error' variant='contained'>
                Apagar
            </Button>
        </DialogActions>
    </Dialog>
);

// One subscriber. Same card at every width — a table would need a horizontal
// scrollbar on a phone.
const SubscriberRow = ({ subscriber, onToggle, onDelete, onCopy }) => (
    <Paper
        variant='outlined'
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: { xs: 1, md: 1.5 },
            borderLeft: 4,
            borderLeftColor: subscriber.isSubscribed ? 'success.main' : 'divider',
        }}
    >
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600, wordBreak: 'break-all' }}>{subscriber.email}</Typography>
                {!subscriber.isSubscribed && (
                    <Chip size='small' variant='outlined' label='ANULADA' sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
            </Box>
            <Typography variant='caption' color='text.secondary' component='div' sx={{ mt: 0.25 }}>
                Subscreveu a {formatDate(subscriber.createdAt)}
                {!subscriber.isSubscribed && ` · anulada a ${formatDate(subscriber.unsubscribedAt)}`}
            </Typography>
        </Box>

        <Tooltip title='Copiar e-mail'>
            <IconButton onClick={() => onCopy(subscriber.email)} aria-label='Copiar e-mail'>
                <ContentCopyIcon fontSize='small' />
            </IconButton>
        </Tooltip>
        <Tooltip title={subscriber.isSubscribed ? 'Anular subscrição (mantém o registo)' : 'Voltar a subscrever'}>
            <IconButton
                color={subscriber.isSubscribed ? 'default' : 'success'}
                onClick={() => onToggle(subscriber)}
                aria-label={subscriber.isSubscribed ? 'Anular subscrição' : 'Voltar a subscrever'}
            >
                {subscriber.isSubscribed ? <UnsubscribeIcon fontSize='small' /> : <MarkEmailReadIcon fontSize='small' />}
            </IconButton>
        </Tooltip>
        <Tooltip title='Apagar de vez'>
            <IconButton color='error' onClick={() => onDelete(subscriber)} aria-label='Apagar subscritor'>
                <DeleteIcon fontSize='small' />
            </IconButton>
        </Tooltip>
    </Paper>
);

// #region SUBSCRIBERS
const Subscribers = () => {
    const dispatch = useDispatch();
    const isMobile = useIsMobile();

    const subscribers = useSelector(selectAllSubscribers);
    const counts = useSelector(selectSubscriberCounts);
    const status = useSelector(selectSubscriberStatus);
    const saving = useSelector(selectSubscriberSaving);
    const error = useSelector(selectSubscriberError);

    const [search, setSearch] = useState('');
    const [onlySubscribed, setOnlySubscribed] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [deleting, setDeleting] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        dispatch(fetchSubscribers());
    }, [dispatch]);

    useEffect(() => {
        if (error) setSnackbar({ open: true, message: error, severity: 'error' });
    }, [error]);

    // Filtering is in memory: the list arrives whole, so narrowing it must not
    // cost a round trip.
    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (subscribers || []).filter(
            (subscriber) => (!term || subscriber.email.includes(term)) && (!onlySubscribed || subscriber.isSubscribed),
        );
    }, [subscribers, search, onlySubscribed]);

    // A new filter shows its own first page, not the previous scroll depth.
    useEffect(() => setVisibleCount(PAGE_SIZE), [search, onlySubscribed]);

    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const handleToggle = useCallback(
        async (subscriber) => {
            const result = await dispatch(setSubscribed({ id: subscriber.idSubscriber, subscribed: !subscriber.isSubscribed }));
            if (setSubscribed.fulfilled.match(result)) {
                notify(subscriber.isSubscribed ? 'Subscrição anulada.' : 'Subscrição reposta.');
            }
        },
        [dispatch],
    );

    const handleDelete = useCallback(async () => {
        const subscriber = deleting;
        setDeleting(null);
        if (!subscriber) return;
        const result = await dispatch(deleteSubscriber(subscriber.idSubscriber));
        if (deleteSubscriber.fulfilled.match(result)) notify(`“${subscriber.email}” apagado.`);
    }, [dispatch, deleting]);

    // Clipboard needs a secure context (https or localhost); on plain http the
    // API is simply absent, so say so instead of failing silently.
    const copy = useCallback(async (text, what = 'E-mail copiado.') => {
        try {
            if (!navigator.clipboard) throw new Error('no clipboard');
            await navigator.clipboard.writeText(text);
            notify(what);
        } catch (err) {
            notify('Não foi possível copiar (o browser só permite em https).', 'warning');
        }
    }, []);

    const handleCopyAll = () => {
        if (!rows.length) return;
        copy(rows.map((subscriber) => subscriber.email).join(', '), `${rows.length} e-mail(s) copiados.`);
    };

    // #region RETURN
    return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: { xs: 1, md: 2 }, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                    size='small'
                    color={onlySubscribed ? 'default' : 'primary'}
                    variant={onlySubscribed ? 'outlined' : 'filled'}
                    label={`Todos: ${counts.total}`}
                    onClick={() => setOnlySubscribed(false)}
                />
                <Chip
                    size='small'
                    color={onlySubscribed ? 'success' : 'default'}
                    variant={onlySubscribed ? 'filled' : 'outlined'}
                    label={`Ativos: ${counts.subscribed}`}
                    onClick={() => setOnlySubscribed(true)}
                />
                <Chip size='small' variant='outlined' label={`Anuladas: ${counts.unsubscribed}`} />
                {(status === 'loading' || saving) && <CircularProgress size={14} />}
                <Box sx={{ flex: 1 }} />
                <Tooltip title='Recarregar'>
                    <IconButton onClick={() => dispatch(fetchSubscribers())} aria-label='Recarregar'>
                        <RefreshIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    placeholder='Procurar e-mail…'
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon fontSize='small' />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position='end'>
                                <IconButton size='small' onClick={() => setSearch('')} aria-label='Limpar procura'>
                                    <ClearIcon fontSize='small' />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <Button
                    size='small'
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyAll}
                    disabled={!rows.length}
                    fullWidth={isMobile}
                    variant='outlined'
                >
                    Copiar lista
                </Button>
                <Button
                    size='small'
                    startIcon={<DownloadIcon />}
                    onClick={() => dispatch(exportSubscribers({ onlySubscribed }))}
                    disabled={!counts.total}
                    fullWidth={isMobile}
                    variant='outlined'
                >
                    Exportar CSV
                </Button>
            </Box>

            <Typography variant='caption' color='text.secondary'>
                Endereços recolhidos pelo formulário do rodapé do site. “Anular subscrição” tira o endereço da lista de envio mas guarda o
                registo; “apagar” elimina-o de vez.
            </Typography>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pb: 2 }}>
                {rows.slice(0, visibleCount).map((subscriber) => (
                    <SubscriberRow
                        key={subscriber.idSubscriber}
                        subscriber={subscriber}
                        onToggle={handleToggle}
                        onDelete={setDeleting}
                        onCopy={copy}
                    />
                ))}

                {rows.length > visibleCount && (
                    <Button onClick={() => setVisibleCount((count) => count + PAGE_SIZE)} sx={{ alignSelf: 'center', mt: 1 }}>
                        Mostrar mais ({rows.length - visibleCount})
                    </Button>
                )}

                {!rows.length && status !== 'loading' && (
                    <Paper variant='outlined' sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color='text.secondary'>
                            {counts.total ? 'Nenhum subscritor corresponde à procura.' : 'Ainda não há subscritores.'}
                        </Typography>
                    </Paper>
                )}
            </Box>

            <DeleteConfirmDialog subscriber={deleting} onClose={() => setDeleting(null)} onConfirm={handleDelete} />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Subscribers;
